import useApi from '../utils/axios-pettitions';
import { useAppSelector } from '../contexts/hooks';
import { newToast } from '../components';
import {
  MaterialControlDTO,
  CreateMaterialControlDTO,
  LoteControlDTO,
  CreateLoteControlDTO,
  ConfiguracionQcDTO,
  CreateConfiguracionQcDTO,
} from '../types/types.qualityControl';

export const useApiConfigQC = () => {
  const { runApi } = useApi();
  const userId = useAppSelector((state) => state.userInfo.User?.Us_Id);

  //#region Materiales de Control

  const getMaterials = async (soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getMaterials', 'GET', { soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as MaterialControlDTO[];
      }
      return [];
    });
  };

  const getMaterialById = async (id: number) => {
    return runApi('qualitycontrol/getMaterialById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const newMaterial = async (material: Omit<CreateMaterialControlDTO, 'Qcm_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateMaterialControlDTO = {
      ...material,
      Qcm_Us_Id: userId,
    };

    return runApi('qualitycontrol/newMaterial', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const putMaterial = async (id: number, material: Partial<CreateMaterialControlDTO>) => {
    return runApi('qualitycontrol/putMaterial', 'POST', { id }, material).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const deleteMaterial = async (id: number) => {
    return runApi('qualitycontrol/deleteMaterial', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Lotes de Control

  const getLotsByMaterial = async (materialId: number, soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getLotsByMaterial', 'GET', { materialId, soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO[];
      }
      return [];
    });
  };

  const getLotById = async (id: number) => {
    return runApi('qualitycontrol/getLotById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const newLot = async (lote: Omit<CreateLoteControlDTO, 'Qcl_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateLoteControlDTO = {
      ...lote,
      Qcl_Us_Id: userId,
    };

    return runApi('qualitycontrol/newLot', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const putLot = async (id: number, lote: Partial<CreateLoteControlDTO>) => {
    return runApi('qualitycontrol/putLot', 'POST', { id }, lote).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const deleteLot = async (id: number) => {
    return runApi('qualitycontrol/deleteLot', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Configuraciones QC

  const getConfigurationsByLot = async (loteId: number, soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getConfigurationsByLot', 'GET', { loteId, soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  const getConfigurationsBySede = async (sedeId: number, soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getConfigurationsBySede', 'GET', { sedeId, soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  const getConfigurationById = async (id: number) => {
    return runApi('qualitycontrol/getConfigurationById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const getActiveConfiguration = async (loteId: number, analitoId: number, sedeId: number) => {
    return runApi('qualitycontrol/getActiveConfiguration', 'GET', { loteId, analitoId, sedeId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const newConfiguration = async (configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateConfiguracionQcDTO = {
      ...configuracion,
      Qcc_Us_Id: userId,
    };

    return runApi('qualitycontrol/newConfiguration', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const putConfiguration = async (id: number, configuracion: Partial<CreateConfiguracionQcDTO>) => {
    return runApi('qualitycontrol/putConfiguration', 'POST', { id }, configuracion).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const endConfigurationValidity = async (id: number) => {
    return runApi('qualitycontrol/endConfigurationValidity', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return false;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  const deleteConfiguration = async (id: number) => {
    return runApi('qualitycontrol/deleteConfiguration', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Endpoints consolidados (evita N+1 desde el frontend)

  const getAllLots = async (soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getAllLots', 'GET', { soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO[];
      }
      return [];
    });
  };

  const getAllConfigurations = async (soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getAllConfigurations', 'GET', { soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  //#endregion

  return {
    getMaterials,
    getMaterialById,
    newMaterial,
    putMaterial,
    deleteMaterial,
    getAllLots,
    getLotsByMaterial,
    getLotById,
    newLot,
    putLot,
    deleteLot,
    getAllConfigurations,
    getConfigurationsByLot,
    getConfigurationsBySede,
    getConfigurationById,
    getActiveConfiguration,
    newConfiguration,
    putConfiguration,
    endConfigurationValidity,
    deleteConfiguration,
  };
};
